import { useEffect } from "react"
import { useDispatch } from "react-redux"
import { useNavigate, useParams } from "react-router-dom"
import { toast } from "react-toastify"
import dayjs from "dayjs"
import dayjs_plugin_timezone from "dayjs/plugin/timezone"

dayjs.extend(dayjs_plugin_timezone)

import { useLazyGetDevicesQuery } from "store/api/devicesApi"
import {
  useLazyGetPerformanceTestReportQuery,
  useLazyGetPerformanceTestSnapshotQuery,
} from "store/api/performanceTestApi"
import { useAppSelector } from "store/hooks"
import { showPerformanceTestReportModal } from "store/modalSlise"
import { setPerformanceTestReport } from "store/tempDataSlise"
import PerformanceTestStatus from "types/enums/PerformanceTestStatus"
import SubscriptionPrivilege from "types/enums/SubscriptionPrivilege"

import hasSubscriptionAccess from "utils/hasSubscriptionAccess"
import { parseDateStringToUTCString } from "utils/parseDateStringToUTCString"
import { PerformanceTestStatusBadge } from "components/common/PerformanceTestStatusBadge"
import { RestrictedAccessWidget } from "components/common/RestrictedAccessWidget"
import { Spinner } from "components/common/Spinner"

import { useCustomerDashboard } from "../../CustomerDashboardContext"
import {
  LoadingContainer,
  NoAlertsText,
  PerformanceTestDate,
  PerformanceTestItem,
  PerformanceTestStatusWrapper,
  PerformanceTestTitle,
  StyledWidgetButton,
  Widget,
  WidgetButton,
  WidgetContent,
  WidgetTitle,
} from "../styled"

export const PerformanceTestSnapshotWidget = () => {
  const dispatch = useDispatch()
  const { devices, currentDevice } = useCustomerDashboard()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [triggerGetSnapshot, { data: snapshotData, isFetching }] = useLazyGetPerformanceTestSnapshotQuery()
  const [triggerGetPerformanceTestReport] = useLazyGetPerformanceTestReportQuery()
  const [triggerGetDevice] = useLazyGetDevicesQuery()
  const userProfile = useAppSelector(state => state.user.userProfile)
  const hasPerformanceTestAccess = userProfile
    ? hasSubscriptionAccess(userProfile, SubscriptionPrivilege.PerformanceTest)
    : false

  const selectedDevice = devices[currentDevice]
  const deviceSN = selectedDevice?.sn

  useEffect(() => {
    if (deviceSN && hasPerformanceTestAccess) {
      triggerGetSnapshot(deviceSN)
    }
  }, [deviceSN, triggerGetSnapshot])

  const handleViewAll = () => {
    navigate(`/customers/${id}/perf-test`)
  }

  const handleStatusClick = async (perftestResultId: number) => {
    if (!selectedDevice) return

    try {
      const deviceData = await triggerGetDevice({ sn: selectedDevice.sn })

      if (deviceData.error) {
        toast.error("Something went wrong, please try again")
        return
      }

      const device = deviceData.data?.data[0]
      const res = await triggerGetPerformanceTestReport(perftestResultId)

      if (res.data && device) {
        dispatch(
          setPerformanceTestReport({
            ...res?.data,
            customer: device.homeowner_name,
            zip_code: device.zip?.code,
            where_installed_name: selectedDevice.name || device?.residence_type?.name,
            timeZone: device.time_zone_text || "",
          }),
        )
        dispatch(showPerformanceTestReportModal(true))
      }
    } catch {
      toast.error("Failed to load performance test report")
    }
  }

  if (!selectedDevice) {
    return null
  }

  const lastPerftest = snapshotData?.data?.last_perftest
  const nextPerftest = snapshotData?.data?.next_perftest
  const hasNextPerftest =
    nextPerftest &&
    [PerformanceTestStatus.SCHEDULED, PerformanceTestStatus.RESCHEDULED, PerformanceTestStatus.RUNNING].includes(
      nextPerftest.schedule_status,
    )

  const timeZone = selectedDevice?.time_zone_text
  const tzSuffix = timeZone ? ` (UTC ${dayjs().tz(timeZone).format("Z")})` : ""
  const formatDateTime = (dateTimeString: string) => {
    return (
      dayjs(parseDateStringToUTCString(dateTimeString))
        .tz(timeZone || "UTC")
        .format("MMM D, YYYY [at] h:mmA") + tzSuffix
    )
  }

  const formatNextPerftestDate = () => {
    if (!nextPerftest) return "-"
    const nextPerftestStatus = nextPerftest.schedule_status

    if (nextPerftestStatus === PerformanceTestStatus.SCHEDULED && nextPerftest.schedule) {
      return formatDateTime(nextPerftest.schedule)
    }

    if (nextPerftestStatus === PerformanceTestStatus.SCHEDULED) {
      const now = dayjs()
      const date = now.hour() < 12 ? now : now.add(1, "day")
      return `${date.format("DD MMM, YYYY")} 10:00 AM - 12:00 PM`
    }

    return "-"
  }

  const renderContent = () => {
    if (isFetching) {
      return (
        <LoadingContainer>
          <Spinner />
        </LoadingContainer>
      )
    }

    if (!lastPerftest && !hasNextPerftest) {
      return <NoAlertsText>No performance test has been scheduled or completed.</NoAlertsText>
    }

    return (
      <>
        <PerformanceTestItem>
          <div>
            <PerformanceTestTitle>Last Test</PerformanceTestTitle>
            <PerformanceTestDate>{lastPerftest ? formatDateTime(lastPerftest.time) : "-"}</PerformanceTestDate>
          </div>
          {lastPerftest && (
            <PerformanceTestStatusWrapper>
              {lastPerftest.status && (
                <PerformanceTestStatusBadge
                  status={lastPerftest.status}
                  onClick={() => {
                    if (["passed", "issue"].includes(lastPerftest.status.toLowerCase())) {
                      handleStatusClick(lastPerftest.perftest_result_id)
                    }
                  }}
                />
              )}
            </PerformanceTestStatusWrapper>
          )}
        </PerformanceTestItem>
        <PerformanceTestItem>
          <div>
            <PerformanceTestTitle>Next Performance Test</PerformanceTestTitle>
            <PerformanceTestDate>{formatNextPerftestDate()}</PerformanceTestDate>
          </div>
          {hasNextPerftest && nextPerftest.schedule_status && (
            <PerformanceTestStatusWrapper>
              <PerformanceTestStatusBadge status={nextPerftest.schedule_status} />
            </PerformanceTestStatusWrapper>
          )}
        </PerformanceTestItem>
      </>
    )
  }

  return (
    <Widget>
      <WidgetTitle>Performance Test Snapshot</WidgetTitle>
      <RestrictedAccessWidget privilege={SubscriptionPrivilege.PerformanceTest}>
        <WidgetContent>{renderContent()}</WidgetContent>
        <WidgetButton>
          <StyledWidgetButton onClick={handleViewAll} size="auto" color="blueBordered">
            View All
          </StyledWidgetButton>
        </WidgetButton>
      </RestrictedAccessWidget>
    </Widget>
  )
}
